import type { Poll } from "@vote/shared";
import type { MyVote, MyVotesFlow, MyVotesFlowState } from "../core/my-votes-flow";
import {
  createElement,
  createShadowRoot,
  createStrokeIcon,
  createSvgElement,
  getChoiceLabel,
  getChoicePercentage,
  getErrorPresentation,
} from "./poll-widget";

// 一覧を差し替えても同じlive regionを維持し、支援技術が追い読み完了を検知できるようにする。
const HISTORY_ANNOUNCEMENT_ID = "vote-history-announcement";
// 記事本文を履歴で埋め尽くさず、最初に判断アーカイブの概要だけを見せる件数。
const INITIAL_VISIBLE_HISTORY_COUNT = 3;
// 1回目は合計10件、その後は10件ずつ表示範囲を広げる段階表示の単位。
const HISTORY_EXPANSION_STEP = 10;

/** 個人の判断を蓄積する画面であることを示す棒グラフアイコンを生成する。 */
function createArchiveIcon(): SVGSVGElement {
  const icon = createStrokeIcon(14, "2", "");
  icon.append(
    createSvgElement("path", { d: "M4 20V10" }),
    createSvgElement("path", { d: "M12 20V4" }),
    createSvgElement("path", { d: "M20 20v-7" }),
  );
  return icon;
}

/** 回答履歴の見出しと各記事カードで共用するクリップボードアイコンを生成する。 */
function createClipboardIcon(showLines: boolean): SVGSVGElement {
  const icon = createStrokeIcon(showLines ? 20 : 14, showLines ? "1.8" : "2", "");
  icon.append(
    createSvgElement("path", {
      d: "M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2",
    }),
    createSvgElement("rect", { x: "8", y: "2", width: "8", height: "4", rx: "1" }),
  );
  if (showLines) {
    icon.append(
      createSvgElement("path", { d: "M9 12h6" }),
      createSvgElement("path", { d: "M9 16h4" }),
    );
  }
  return icon;
}

/** 記事へ遷移できることを視覚的に補足する右向きシェブロンを生成する。 */
function createChevronIcon(): SVGSVGElement {
  const icon = createStrokeIcon(16, "2.5", "text-gray-300");
  icon.append(createSvgElement("path", { d: "M9 6l6 6-6 6" }));
  return icon;
}

/** ISO日時を閲覧者のローカル日付として YYYY.MM.DD 形式にする。 */
export function formatVotedAt(votedAt: string): string {
  const date = new Date(votedAt);
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}.${month}.${day}`;
}

/**
 * サーバが返した割合から最多回答の表示文を返す。同率ならAPIの選択肢順で先にあるものを採用する。
 * 票数ではなく割合を使い、人数を隠す percentages でも同じ規則で出す（F-07。99票以下では
 * 整数%の大小が票数の大小と一致する）。
 */
export function getLeadingChoiceLabel(poll: Poll, percentages: Record<string, number>): string {
  const firstChoice = poll.choices[0];
  if (firstChoice === undefined) {
    return "集計結果不明";
  }
  let leadingChoice = firstChoice;
  for (const choice of poll.choices.slice(1)) {
    if (
      getChoicePercentage(percentages, choice.id) >
      getChoicePercentage(percentages, leadingChoice.id)
    ) {
      leadingChoice = choice;
    }
  }
  return `${leadingChoice.label}が最多`;
}

/** APIから取得済みの履歴だけを、現在の画面上の表示上限まで返す。 */
export function getVisibleHistoryVotes(
  state: Extract<MyVotesFlowState, { kind: "list" }>,
  visibleLimit: number,
): MyVote[] {
  return state.votes.slice(0, visibleLimit);
}

/** 初回3件の次は合計10件、その後は10件ずつ増える次の表示上限を返す。 */
export function getNextVisibleHistoryLimit(currentLimit: number): number {
  return currentLimit === INITIAL_VISIBLE_HISTORY_COUNT
    ? HISTORY_EXPANSION_STEP
    : currentLimit + HISTORY_EXPANSION_STEP;
}

/** 非表示の取得済み履歴か次ページが残っている間だけ「もっと見る」を出す。 */
export function hasMoreHistoryToShow(
  state: Extract<MyVotesFlowState, { kind: "list" }>,
  visibleLimit: number,
): boolean {
  return visibleLimit < state.votes.length || state.nextCursor !== null;
}

/** 次の表示段階を満たすために、追加のAPI取得が必要かを決める。 */
export function decideHistoryExpansion(
  state: Extract<MyVotesFlowState, { kind: "list" }>,
  currentLimit: number,
): { visibleLimit: number; shouldFetch: boolean } {
  const visibleLimit = getNextVisibleHistoryLimit(currentLimit);
  return {
    visibleLimit,
    // 取得済み20件を先に使い、次の表示上限を満たせない場合だけcursorを消費する。
    shouldFetch: visibleLimit > state.votes.length && state.nextCursor !== null,
  };
}

/** 新たに画面へ出た件数だけを伝える読み上げ文を作る。 */
function addedHistoryAnnouncement(addedCount: number): string {
  return addedCount > 0 ? `${addedCount}件を読み込みました` : "";
}

/** 追い読み前後の表示件数差から、追加分だけを伝える読み上げ文を作る。 */
export function getHistoryAnnouncement(
  state: MyVotesFlowState,
  previousState?: MyVotesFlowState,
  visibleCount = state.kind === "list" ? state.votes.length : 0,
  previousVisibleCount = previousState?.kind === "list" ? previousState.votes.length : 0,
): string {
  if (state.kind !== "list" || previousState?.kind !== "list" || !previousState.isLoadingMore) {
    return "";
  }
  return addedHistoryAnnouncement(visibleCount - previousVisibleCount);
}

/** デザイン原本と全状態で共用する、個人画面の外側カードを生成する。 */
function createCard(): HTMLElement {
  return createElement(
    "section",
    "flex flex-col gap-3.5 rounded-xl border border-gray-200 bg-white p-5 font-sans text-gray-900 shadow-[0_1px_2px_rgba(17,24,39,0.05)]",
  );
}

/** ブランド名と判断アーカイブを一行のヘッダーとして生成する。 */
function createHeader(): HTMLElement {
  const header = createElement("header", "flex min-w-0 items-center gap-2");
  const icon = createElement(
    "span",
    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600",
  );
  icon.append(createArchiveIcon());
  const title = createElement("h2", "shrink-0 text-[15px] font-bold text-gray-900", "私の");
  title.append(createElement("span", "text-pink-600", "Vote"));
  header.append(
    icon,
    title,
    createElement("span", "truncate text-xs text-gray-500", "判断アーカイブ"),
  );
  return header;
}

/** 一覧の内容を示す小見出しをアイコン付きで生成する。 */
function createHistoryHeading(): HTMLElement {
  const heading = createElement("div", "flex items-center gap-1.5 text-gray-600");
  heading.append(
    createClipboardIcon(false),
    createElement("h3", "text-xs font-semibold", "最近回答したアンケート"),
  );
  return heading;
}

/** 本人回答または全体最多を、長い選択肢名でも折り返せる小バッジにする。 */
function createAnswerBadge(text: string, mine: boolean): HTMLElement {
  return createElement(
    "span",
    [
      "inline-block max-w-48 truncate rounded-full px-2 py-0.5 text-[11px] font-semibold",
      mine ? "bg-pink-50 text-pink-600" : "bg-gray-100 text-gray-600",
    ].join(" "),
    text,
  );
}

/** 1件の履歴全体を回答元記事へのリンクにし、設問・回答・最多・日付を表示する。 */
function createHistoryItem(vote: MyVote): HTMLLIElement {
  const item = createElement("li");
  const link = createElement(
    "a",
    "flex min-w-0 cursor-pointer items-center gap-3 rounded-[10px] border border-gray-200 p-3.5 text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600",
  );
  // pageUrlはサーバで保存時に正規化済みの契約値であり、SDK側で書き換えずそのまま記事遷移へ使う。
  link.href = vote.pageUrl;

  const itemIcon = createElement(
    "span",
    "flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border border-gray-100 bg-gray-50 text-gray-400",
  );
  itemIcon.append(createClipboardIcon(true));

  const content = createElement("span", "flex min-w-0 flex-1 flex-col gap-1.5");
  content.append(createElement("span", "text-[13px] font-bold leading-5", vote.poll.question));
  const answers = createElement("span", "flex min-w-0 flex-wrap items-center gap-1.5");
  answers.append(
    createElement("span", "text-[11px] text-gray-500", "あなたの回答"),
    createAnswerBadge(getChoiceLabel(vote.poll, vote.myChoiceId), true),
  );
  // 「全体」の欄には公開された割合の最多を出す。集計中（受付中の10票未満）は傾向を出さず、
  // 同じ欄に状態だけを灰色バッジで示す（D-059 補記。終了した投票はサーバが割合を返すので最多が出る）。
  answers.append(
    createElement("span", "text-[11px] text-gray-500", "全体"),
    createAnswerBadge(
      vote.results.kind === "collecting"
        ? "初期集計中"
        : getLeadingChoiceLabel(vote.poll, vote.results.percentages),
      false,
    ),
  );
  content.append(answers);

  const destination = createElement("span", "flex shrink-0 flex-col items-end gap-1.5");
  destination.append(
    createElement("span", "text-[11px] text-gray-400", formatVotedAt(vote.votedAt)),
  );
  destination.append(createChevronIcon());
  link.append(itemIcon, content, destination);
  item.append(link);
  return item;
}

/** 初回通信中にも完成後と近い高さを保つスケルトン一覧を生成する。 */
function renderLoading(): HTMLElement {
  const card = createCard();
  card.setAttribute("role", "status");
  card.setAttribute("aria-live", "polite");
  const skeletons = createElement("div", "flex flex-col gap-3");
  for (let index = 0; index < 3; index += 1) {
    skeletons.append(
      createElement(
        "div",
        "h-[74px] animate-[pulse_1.6s_ease-in-out_infinite] rounded-[10px] border border-gray-100 bg-gray-50",
      ),
    );
  }
  card.append(
    createHeader(),
    createHistoryHeading(),
    skeletons,
    createElement("p", "text-center text-[11px] text-gray-400", "回答履歴を読み込んでいます…"),
  );
  return card;
}

/** 回答がまだない正常状態を、失敗と区別できる案内として表示する。 */
function renderEmpty(): HTMLElement {
  const card = createCard();
  const message = createElement(
    "div",
    "flex flex-col items-center gap-1.5 rounded-[10px] border border-gray-100 bg-gray-50 p-5 text-center",
  );
  message.append(
    createElement("p", "text-sm font-semibold text-gray-700", "まだ回答したアンケートがありません"),
    createElement("p", "text-xs text-gray-500", "記事の投票に回答すると、ここに履歴が表示されます"),
  );
  card.append(createHeader(), createHistoryHeading(), message);
  return card;
}

/** 現在の上限まで履歴を表示し、画面またはAPIに続きがある間だけ展開ボタンを追加する。 */
function renderList(
  state: Extract<MyVotesFlowState, { kind: "list" }>,
  visibleLimit: number,
  onShowMore: () => void,
): HTMLElement {
  const card = createCard();
  const list = createElement("ul", "flex flex-col gap-3");
  for (const vote of getVisibleHistoryVotes(state, visibleLimit)) {
    list.append(createHistoryItem(vote));
  }
  card.append(createHeader(), createHistoryHeading(), list);

  if (hasMoreHistoryToShow(state, visibleLimit)) {
    const button = createElement(
      "button",
      "flex h-11 w-full items-center justify-center rounded-full px-5 text-[13px] font-semibold text-pink-600 hover:bg-pink-50 hover:text-pink-700 disabled:cursor-wait disabled:text-pink-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600",
      state.isLoadingMore ? "読み込み中…" : "もっと見る",
    );
    button.type = "button";
    button.disabled = state.isLoadingMore;
    button.addEventListener("click", onShowMore);
    card.append(button);
  }
  return card;
}

/** 投票画面と同じエラー分類を使い、回復可能な通信失敗だけ再試行を表示する。 */
function renderError(
  state: Extract<MyVotesFlowState, { kind: "error" }>,
  flow: MyVotesFlow,
): HTMLElement {
  const card = createCard();
  card.setAttribute("role", "alert");
  const presentation = getErrorPresentation(state.code);
  const message = createElement("div", "flex flex-col items-center gap-2 py-4 text-center");
  message.append(
    createElement("h3", "text-sm font-semibold text-gray-700", presentation.title),
    createElement("p", "text-xs text-gray-500", presentation.description),
  );
  card.append(createHeader(), message);
  if (presentation.retryable) {
    const retryButton = createElement(
      "button",
      "flex h-11 w-full items-center justify-center rounded-full border border-pink-600 bg-white px-6 text-sm font-bold text-pink-600 hover:bg-pink-50 hover:text-pink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600",
      "再試行",
    );
    retryButton.type = "button";
    retryButton.addEventListener("click", () => void flow.retry());
    card.append(retryButton);
  }
  return card;
}

/** 排他的なcore状態を、それぞれの個人画面へ振り分ける。 */
function renderState(
  state: MyVotesFlowState,
  flow: MyVotesFlow,
  visibleLimit: number,
  onShowMore: () => void,
): HTMLElement {
  switch (state.kind) {
    case "loading":
      return renderLoading();
    case "list":
      return renderList(state, visibleLimit, onShowMore);
    case "empty":
      return renderEmpty();
    case "error":
      return renderError(state, flow);
  }
}

/**
 * 個人画面枠へShadow DOMを作り、履歴状態と操作を結び付ける。
 * 戻り値は将来動的に枠を外す場合に使える購読解除関数。
 */
export function mountMyPageWidget(
  container: HTMLElement,
  flow: MyVotesFlow,
  widgetStyles: string,
): () => void {
  const shadow = createShadowRoot(container, widgetStyles);
  if (shadow === null) {
    return () => undefined;
  }
  const root = createElement("div", "text-gray-900");
  const liveRegion = createElement("div", "sr-only");
  liveRegion.id = HISTORY_ANNOUNCEMENT_ID;
  liveRegion.setAttribute("role", "status");
  liveRegion.setAttribute("aria-live", "polite");
  liveRegion.setAttribute("aria-atomic", "true");
  shadow.append(root, liveRegion);
  let previousState: MyVotesFlowState | undefined;
  let currentState = flow.getState();
  let visibleLimit = INITIAL_VISIBLE_HISTORY_COUNT;
  let previousVisibleCount = 0;
  // 追加取得が失敗しても同じ表示段階を再試行できるよう、成功まで上限と分けて保持する。
  let pendingVisibleLimit: number | null = null;

  /** coreの最新状態と現在の表示上限を使って、個人画面だけを再描画する。 */
  function renderCurrentState(): void {
    root.replaceChildren(renderState(currentState, flow, visibleLimit, handleShowMore));
  }

  /** 表示上限を次段階へ進め、取得済み件数だけでは足りない場合だけ次ページを要求する。 */
  function handleShowMore(): void {
    if (currentState.kind !== "list" || currentState.isLoadingMore) {
      return;
    }

    const oldVisibleCount = getVisibleHistoryVotes(currentState, visibleLimit).length;
    const expansion = decideHistoryExpansion(currentState, visibleLimit);
    if (expansion.shouldFetch) {
      // 失敗時には上限を進めず、次の操作が同じ段階の再試行になるよう保留する。
      pendingVisibleLimit = expansion.visibleLimit;
      void flow.loadMore();
      return;
    }

    visibleLimit = expansion.visibleLimit;
    renderCurrentState();
    const nextVisibleCount = getVisibleHistoryVotes(currentState, visibleLimit).length;
    liveRegion.textContent = addedHistoryAnnouncement(nextVisibleCount - oldVisibleCount);
    previousVisibleCount = nextVisibleCount;
  }

  return flow.subscribe((state) => {
    // APIの詳細は画面に露出させず、導入者が調査できる情報だけコンソールへ残す。
    if (state.kind === "error") {
      console.warn(`[vote-sdk] ${state.code}: ${state.message}`);
    }

    // 初回取得や投票後の再取得では、履歴の先頭3件から見直せる状態へ戻す。
    if (state.kind === "loading") {
      visibleLimit = INITIAL_VISIBLE_HISTORY_COUNT;
      pendingVisibleLimit = null;
    }

    // 追加取得が成功したときだけ、クリック時に保留した次の表示上限を確定する。
    if (
      state.kind === "list" &&
      previousState?.kind === "list" &&
      previousState.isLoadingMore &&
      !state.isLoadingMore &&
      pendingVisibleLimit !== null
    ) {
      if (state.votes.length > previousState.votes.length) {
        visibleLimit = pendingVisibleLimit;
      }
      pendingVisibleLimit = null;
    }

    const oldVisibleCount = previousVisibleCount;
    currentState = state;
    renderCurrentState();
    const nextVisibleCount =
      state.kind === "list" ? getVisibleHistoryVotes(state, visibleLimit).length : 0;
    liveRegion.textContent = getHistoryAnnouncement(
      state,
      previousState,
      nextVisibleCount,
      oldVisibleCount,
    );
    previousVisibleCount = nextVisibleCount;
    previousState = state;
  });
}
